import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Network, Users, Building2, ChevronDown, ChevronRight, List, GitBranch,
  GraduationCap, CheckCircle2, Clock, Globe, Infinity, ZoomIn, ZoomOut, Copy, Filter, Search, Maximize2, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function PersonaCapacitaciones({ personaId, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCaps = useCallback(async function() {
    setLoading(true);
    try {
      const res = await axios.get(API_BASE + '/api/personas/' + personaId + '/capacitaciones-requeridas');
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [personaId]);

  useEffect(function() {
    if (personaId) fetchCaps();
  }, [personaId, fetchCaps]);

  const toggle = async function(capId, done) {
    try {
      const action = done ? 'desmarcar' : 'completar';
      await axios.post(API_BASE + '/api/personas/' + personaId + '/capacitaciones/' + capId + '/' + action);
      toast.success(done ? 'Desmarcada' : 'Completada');
      fetchCaps();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Error');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-4"><div className="animate-spin h-5 w-5 border-b-2 border-teal-500 rounded-full"></div></div>;
  }

  if (!data || !data.capacitaciones || data.capacitaciones.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-4">No hay capacitaciones</p>;
  }

  const caps = data.capacitaciones;
  const stats = data.resumen;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">Cumplimiento</span>
          <span className="font-medium">{stats.cumplidas}/{stats.total} ({stats.porcentaje}%)</span>
        </div>
        <Progress value={stats.porcentaje} className="h-2" />
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {caps.map(function(cap) {
          const done = cap.cumplida;
          const bgClass = done ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
          const btnClass = done ? 'bg-green-500 text-white' : 'bg-white border-2 border-red-300';
          const txtClass = done ? 'text-green-800' : 'text-red-800';
          
          return (
            <div key={cap.id} className={'flex gap-2 p-2 rounded-lg border ' + bgClass}>
              <button onClick={function() { toggle(cap.id, done); }} className={'mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ' + btnClass}>
                {done && <CheckCircle2 className="w-3 h-3" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={'text-xs font-medium ' + txtClass}>{cap.nombre}</p>
                <div className="flex gap-2 mt-0.5">
                  {cap.es_transversal ? (
                    <span className="text-[10px] flex items-center gap-0.5 text-purple-600"><Globe className="w-2.5 h-2.5" />Transversal</span>
                  ) : (
                    <span className="text-[10px] flex items-center gap-0.5 text-blue-600"><Building2 className="w-2.5 h-2.5" />Área</span>
                  )}
                  {cap.vigencia_meses ? (
                    <span className="text-[10px] flex items-center gap-0.5 text-slate-500"><Clock className="w-2.5 h-2.5" />{cap.vigencia_meses}m</span>
                  ) : (
                    <span className="text-[10px] flex items-center gap-0.5 text-emerald-600"><Infinity className="w-2.5 h-2.5" />Indefinida</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TreeNode({ cargo, areas, personas, cargos, expanded, onToggle, onSelect, selectedId, visibleCargoIds, filteredPersonas, isFiltered, cargoIdsWithFilteredPersonas, highlightedCargoId, onShowPersonas, lockedGerencia }) {
  function getAreaName(areaId) {
    for (var i = 0; i < areas.length; i++) {
      if (areas[i].id === areaId) return areas[i].nombre;
    }
    return '-';
  }

  function getPersonasByCargo(cargoId) {
    if (!isFiltered) {
      var result = [];
      for (var i = 0; i < personas.length; i++) {
        if (personas[i].cargo_id === cargoId) result.push(personas[i]);
      }
      return result;
    }
    if (cargoIdsWithFilteredPersonas && cargoIdsWithFilteredPersonas.has(cargoId)) {
      var result2 = [];
      for (var j = 0; j < filteredPersonas.length; j++) {
        if (filteredPersonas[j].cargo_id === cargoId) result2.push(filteredPersonas[j]);
      }
      return result2;
    }
    var result3 = [];
    for (var k = 0; k < personas.length; k++) {
      if (personas[k].cargo_id === cargoId) result3.push(personas[k]);
    }
    return result3;
  }

  function getSubCargos(cargoId) {
    var result = [];
    for (var i = 0; i < cargos.length; i++) {
      if (cargos[i].cargo_superior_id === cargoId) {
        if (!visibleCargoIds || visibleCargoIds.has(cargos[i].id)) {
          result.push(cargos[i]);
        } else if (lockedGerencia) {
          var nested = getVisibleDescendants(cargos[i].id);
          for (var j = 0; j < nested.length; j++) result.push(nested[j]);
        }
      }
    }
    return result;
  }

  function getVisibleDescendants(cargoId) {
    var out = [];
    for (var i = 0; i < cargos.length; i++) {
      if (cargos[i].cargo_superior_id === cargoId) {
        if (visibleCargoIds && visibleCargoIds.has(cargos[i].id)) {
          out.push(cargos[i]);
        } else {
          var n = getVisibleDescendants(cargos[i].id);
          for (var j = 0; j < n.length; j++) out.push(n[j]);
        }
      }
    }
    return out;
  }

  function getNodeWidth(node) {
    var subs = [];
    for (var i = 0; i < cargos.length; i++) {
      if (cargos[i].cargo_superior_id === node.id && !cargos[i].es_staff) {
        if (!visibleCargoIds || visibleCargoIds.has(cargos[i].id)) {
          subs.push(cargos[i]);
        } else if (lockedGerencia) {
          var nestedW = getVisibleDescendants(cargos[i].id);
          for (var jj = 0; jj < nestedW.length; jj++) if (!nestedW[jj].es_staff) subs.push(nestedW[jj]);
        }
      }
    }
    if (subs.length === 0) return 300;
    var total = 0;
    for (var j = 0; j < subs.length; j++) {
      total += getNodeWidth(subs[j]);
    }
    return Math.max(300, total);
  }

  var subCargos = getSubCargos(cargo.id);
  var staffCargos = subCargos.filter(function(c) { return c.es_staff; });
  var lineCargos = subCargos.filter(function(c) { return !c.es_staff; });
  var cargoPersonas = getPersonasByCargo(cargo.id);
  var hasChildren = lineCargos.length > 0;
  var isExpanded = expanded[cargo.id] === true;
  var isSelected = selectedId === cargo.id;
  var isHighlighted = highlightedCargoId === cargo.id;

  return (
    <div className="flex flex-col items-center">
      {/* Main node centered */}
      <div 
        className={'relative border-2 rounded-xl p-3 min-w-[200px] max-w-[280px] cursor-pointer hover:border-teal-400 transition-all ' + (isHighlighted ? 'bg-amber-50 border-amber-400 shadow-xl ring-2 ring-amber-300 animate-pulse' : isSelected ? 'bg-white border-teal-500 shadow-lg' : 'bg-white border-slate-200')}
        onClick={function() { onSelect(cargo); }}
        data-testid={'tree-node-' + cargo.id}
      >
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-teal-500 text-white text-[10px] font-medium rounded-full">
          Nivel {cargo.nivel}
        </div>
        
        <div className="flex flex-col items-center mt-1 text-center">
          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mb-1">
            <Network className="w-4 h-4 text-teal-600" />
          </div>
          <div className="min-w-0 w-full">
            <p className="text-xs font-bold text-slate-900 leading-snug">{cargo.nombre}</p>
            <p className="text-[10px] text-slate-500">{getAreaName(cargo.area_id)}</p>
            {cargoPersonas.length > 0 && (
              <div className="mt-1">
                {cargoPersonas.slice(0, 2).map(function(p, idx) {
                  return (
                    <p key={idx} className="text-[10px] text-teal-600 font-medium leading-snug">
                      {p.nombre} {p.apellido}
                    </p>
                  );
                })}
                {cargoPersonas.length > 2 && (
                  <button type="button"
                    className="text-[10px] text-teal-500 font-semibold hover:text-teal-700 hover:underline cursor-pointer"
                    onClick={function(e) { e.stopPropagation(); onShowPersonas(cargo, cargoPersonas); }}>
                    +{cargoPersonas.length - 2} más
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {(hasChildren || staffCargos.length > 0) && (
          <button
            className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 bg-white border border-slate-300 rounded-full flex items-center justify-center hover:bg-slate-100 z-10 shadow-sm"
            onClick={function(e) { e.stopPropagation(); onToggle(cargo.id); }}
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* Vertical connector + staff branching from it + children */}
      {(hasChildren || staffCargos.length > 0) && isExpanded && (
        <div className="flex flex-col items-center">
          {/* Staff branches: vertical line with horizontal branch to the right */}
          {staffCargos.length > 0 ? (
            <>
              <div className="relative">
                {/* Vertical line segment - behind the expand button */}
                <div className="w-[2px] bg-slate-400 mx-auto" style={{ height: staffCargos.length * 70 + 'px', position: 'relative', zIndex: 0 }}></div>
                {/* Staff nodes positioned absolutely to the right */}
                {staffCargos.map(function(staff, sIdx) {
                  var staffPersonas = getPersonasByCargo(staff.id);
                  var isStaffSelected = selectedId === staff.id;
                  var isStaffHighlighted = highlightedCargoId === staff.id;
                  return (
                    <div key={staff.id} className="absolute flex items-center" style={{ top: (sIdx * 70 + 12) + 'px', left: '1px' }}>
                      <div className="bg-slate-400" style={{ width: '80px', height: '2px' }}></div>
                      <div
                        className={'relative border-2 rounded-lg p-2.5 cursor-pointer hover:border-teal-400 transition-all whitespace-nowrap ' + (isStaffHighlighted ? 'bg-teal-50 border-teal-400 shadow-xl ring-2 ring-teal-300 animate-pulse' : isStaffSelected ? 'bg-white border-teal-500 shadow-lg' : 'bg-white border-slate-200')}
                        onClick={function() { onSelect(staff); }}
                        data-testid={'tree-node-' + staff.id}
                      >
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0 bg-teal-500 text-white text-[8px] font-medium rounded-full">Staff</div>
                        <div className="text-center mt-1">
                          <p className="text-[11px] font-bold text-slate-800 leading-snug">{staff.nombre}</p>
                          {staffPersonas.length > 0 && staffPersonas.slice(0, 1).map(function(p, idx) {
                            return <p key={idx} className="text-[10px] text-teal-600 font-medium">{p.nombre} {p.apellido}</p>;
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="w-[2px] h-6 bg-slate-400"></div>
          )}

          {/* Children below */}
          {hasChildren && (
            <>
              {staffCargos.length === 0 && null}
              {staffCargos.length > 0 && <div className="w-[2px] h-4 bg-slate-400"></div>}
              {lineCargos.length > 1 && (
                <div className="flex flex-col items-center">
                  {/* Horizontal bar connecting all children */}
                  <div className="flex">
                    {lineCargos.map(function(sub, idx) {
                      var isFirst = idx === 0;
                      var isLast = idx === lineCargos.length - 1;
                      var colWidth = getNodeWidth(sub);
                      return (
                        <div key={'bar-' + sub.id} style={{ width: colWidth + 'px' }}>
                          <div className="flex h-[2px]">
                            <div className={'flex-1' + (isFirst ? '' : ' bg-slate-400')}></div>
                            <div className={'flex-1' + (isLast ? '' : ' bg-slate-400')}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Children with vertical drops */}
                  <div className="flex">
                    {lineCargos.map(function(sub) {
                      var colWidth = getNodeWidth(sub);
                      return (
                        <div key={sub.id} className="flex flex-col items-center" style={{ width: colWidth + 'px' }}>
                          <div className="w-[2px] h-5 bg-slate-400"></div>
                          <TreeNode
                            cargo={sub} areas={areas} personas={personas} cargos={cargos}
                            expanded={expanded} onToggle={onToggle} onSelect={onSelect} selectedId={selectedId}
                            visibleCargoIds={visibleCargoIds} filteredPersonas={filteredPersonas}
                            isFiltered={isFiltered} cargoIdsWithFilteredPersonas={cargoIdsWithFilteredPersonas}
                            highlightedCargoId={highlightedCargoId}
                            onShowPersonas={onShowPersonas}
                            lockedGerencia={lockedGerencia}                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {lineCargos.length === 1 && (
                <div className="flex flex-col items-center">
                  <div className="w-[2px] h-5 bg-slate-400"></div>
                  <TreeNode
                    cargo={lineCargos[0]} areas={areas} personas={personas} cargos={cargos}
                    expanded={expanded} onToggle={onToggle} onSelect={onSelect} selectedId={selectedId}
                    visibleCargoIds={visibleCargoIds} filteredPersonas={filteredPersonas}
                    isFiltered={isFiltered} cargoIdsWithFilteredPersonas={cargoIdsWithFilteredPersonas}
                    highlightedCargoId={highlightedCargoId}
                    onShowPersonas={onShowPersonas}
                    lockedGerencia={lockedGerencia}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function Organigrama({ hideCapacitaciones, defaultZoom, defaultGerenciaId, lockedGerencia, hideDetalle }) {
  const [cargos, setCargos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [gerenciasList, setGerenciasList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCargo, setSelectedCargo] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [viewMode, setViewMode] = useState('tree');
  const [zoom, setZoom] = useState(defaultZoom || 100);
  const [filterGerencia, setFilterGerencia] = useState(defaultGerenciaId || '');
  const [filterSuperintendencia, setFilterSuperintendencia] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [highlightedCargoId, setHighlightedCargoId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [personasModal, setPersonasModal] = useState({ open: false, cargo: null, personas: [] });

  const fetchData = useCallback(async function() {
    try {
      var responses = await Promise.all([
        axios.get(API_BASE + '/api/cargos'),
        axios.get(API_BASE + '/api/areas'),
        axios.get(API_BASE + '/api/personas'),
        axios.get(API_BASE + '/api/gerencias')
      ]);
      var cargosList = responses[0].data;
      var areasList = responses[1].data;
      var personasList = responses[2].data;
      var gerenciasData = responses[3].data || [];
      
      var personasFiltradas = personasList.filter(function(p) {
        return p.mostrar_en_organigrama !== false;
      });

      // Build set of cargo_ids that have at least one persona
      var cargosConPersona = new Set();
      personasFiltradas.forEach(function(p) {
        if (p.cargo_id) cargosConPersona.add(p.cargo_id);
      });

      // Filter cargos: only show those with persona assigned AND proper config
      // Nivel 1: needs persona (no superior required)
      // Nivel > 1: needs persona + cargo_superior (gerencia_id is optional - se infiere de la persona)
      var cargosCompletos = cargosList.filter(function(c) {
        if (!cargosConPersona.has(c.id)) return false;
        if (c.nivel === 1) return true;
        return !!c.cargo_superior_id;
      });

      // Also include ancestor cargos (cargo_superior) even if they don't have persona,
      // so the tree stays connected
      var cargoMap = {};
      cargosList.forEach(function(c) { cargoMap[c.id] = c; });
      var finalIds = new Set(cargosCompletos.map(function(c) { return c.id; }));
      cargosCompletos.forEach(function(c) {
        var current = c;
        while (current && current.cargo_superior_id) {
          if (finalIds.has(current.cargo_superior_id)) break;
          var parent = cargoMap[current.cargo_superior_id];
          if (parent) {
            finalIds.add(parent.id);
          }
          current = parent;
        }
      });
      var cargosFinal = cargosList.filter(function(c) { return finalIds.has(c.id); });
      
      setCargos(cargosFinal);
      setAreas(areasList);
      setPersonas(personasFiltradas);
      setGerenciasList(gerenciasData);
      
      var exp = {};
      cargosFinal.forEach(function(c) { exp[c.id] = true; });
      setExpanded(exp);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);

  function getAreaName(areaId) {
    for (var i = 0; i < areas.length; i++) {
      if (areas[i].id === areaId) return areas[i].nombre;
    }
    return '-';
  }

  // Map cargo_id -> cargo (source of truth for gerencia hierarchy)
  var cargoMap = {};
  cargos.forEach(function(c) { cargoMap[c.id] = c; });

  // gerencia_id -> nombre lookup (autoritativo desde /api/gerencias)
  var gerenciaNameMap = {};
  gerenciasList.forEach(function(g) { gerenciaNameMap[g.id] = g.nombre; });

  // gerencia_id efectiva de una persona = gerencia del cargo (fuente de verdad)
  // con fallback al gerencia_id de la persona si el cargo no tiene gerencia.
  function personaGerenciaId(p) {
    if (p.cargo_id && cargoMap[p.cargo_id] && cargoMap[p.cargo_id].gerencia_id) {
      return cargoMap[p.cargo_id].gerencia_id;
    }
    return p.gerencia_id || null;
  }

  // Build gerencia/superintendencia options from personas data
  var gerenciaOptions = [];
  var supOptions = [];
  var gerSeen = {};
  var supSeen = {};
  personas.forEach(function(p) {
    var gid = personaGerenciaId(p);
    if (gid && !gerSeen[gid]) {
      gerSeen[gid] = true;
      var nombre = gerenciaNameMap[gid] || p.gerencia_nombre || gid;
      gerenciaOptions.push({ id: gid, nombre: nombre });
    }
    if (p.superintendencia_id && !supSeen[p.superintendencia_id]) {
      supSeen[p.superintendencia_id] = true;
      supOptions.push({ id: p.superintendencia_id, nombre: p.superintendencia_nombre || p.superintendencia_id });
    }
  });
  gerenciaOptions.sort(function(a, b) { return a.nombre.localeCompare(b.nombre); });
  supOptions.sort(function(a, b) { return a.nombre.localeCompare(b.nombre); });

  // Filtered personas and cargos based on gerencia/superintendencia
  var filteredPersonas = personas;
  if (filterGerencia) {
    filteredPersonas = filteredPersonas.filter(function(p) { return personaGerenciaId(p) === filterGerencia; });
  }
  if (filterSuperintendencia) {
    filteredPersonas = filteredPersonas.filter(function(p) { return p.superintendencia_id === filterSuperintendencia; });
  }

  var isFiltered = filterGerencia || filterSuperintendencia;

  // Get cargo IDs that have filtered personas + all their ancestors
  var cargoIdsWithFilteredPersonas = new Set(); // cargos that directly have filtered personas
  
  function getVisibleCargoIds() {
    if (!isFiltered) return null; // show all
    var cargoIds = new Set();
    filteredPersonas.forEach(function(p) {
      if (p.cargo_id) {
        cargoIds.add(p.cargo_id);
        cargoIdsWithFilteredPersonas.add(p.cargo_id);
      }
    });
    // Walk up to ancestors
    var cargoMap = {};
    cargos.forEach(function(c) { cargoMap[c.id] = c; });
    var toProcess = Array.from(cargoIds);
    while (toProcess.length > 0) {
      var cid = toProcess.pop();
      var cargo = cargoMap[cid];
      if (cargo && cargo.cargo_superior_id && !cargoIds.has(cargo.cargo_superior_id)) {
        cargoIds.add(cargo.cargo_superior_id);
        toProcess.push(cargo.cargo_superior_id);
      }
    }
    // skipIntermediateAncestors (lockedGerencia): mantener solo cargos directos + N1 raíz
    if (lockedGerencia && filterGerencia) {
      var keep = new Set();
      cargoIdsWithFilteredPersonas.forEach(function(id) { keep.add(id); });
      cargoIds.forEach(function(id) {
        var cc = cargoMap[id];
        if (cc && !cc.cargo_superior_id) keep.add(id);
      });
      return keep;
    }
    return cargoIds;
  }

  var visibleCargoIds = getVisibleCargoIds();

  // Filtered superintendent list based on selected gerencia
  var filteredSuperintendencias = supOptions;
  if (filterGerencia) {
    var gerPersonas = personas.filter(function(p) { return personaGerenciaId(p) === filterGerencia; });
    var supIds = new Set();
    gerPersonas.forEach(function(p) { if (p.superintendencia_id) supIds.add(p.superintendencia_id); });
    filteredSuperintendencias = supOptions.filter(function(s) { return supIds.has(s.id); });
  }

  function getPersonasByCargo(cargoId) {
    if (!isFiltered) {
      var result = [];
      for (var i = 0; i < personas.length; i++) {
        if (personas[i].cargo_id === cargoId) result.push(personas[i]);
      }
      return result;
    }
    if (cargoIdsWithFilteredPersonas.has(cargoId)) {
      var result2 = [];
      for (var j = 0; j < filteredPersonas.length; j++) {
        if (filteredPersonas[j].cargo_id === cargoId) result2.push(filteredPersonas[j]);
      }
      return result2;
    }
    var result3 = [];
    for (var k = 0; k < personas.length; k++) {
      if (personas[k].cargo_id === cargoId) result3.push(personas[k]);
    }
    return result3;
  }

  function getSubCargos(cargoId) {
    var result = [];
    for (var i = 0; i < cargos.length; i++) {
      if (cargos[i].cargo_superior_id === cargoId) {
        if (!visibleCargoIds || visibleCargoIds.has(cargos[i].id)) {
          result.push(cargos[i]);
        } else if (lockedGerencia) {
          var nested = getVisibleDescendantsOuter(cargos[i].id);
          for (var j = 0; j < nested.length; j++) result.push(nested[j]);
        }
      }
    }
    return result;
  }

  function getVisibleDescendantsOuter(cargoId) {
    var out = [];
    for (var i = 0; i < cargos.length; i++) {
      if (cargos[i].cargo_superior_id === cargoId) {
        if (visibleCargoIds && visibleCargoIds.has(cargos[i].id)) {
          out.push(cargos[i]);
        } else {
          var n = getVisibleDescendantsOuter(cargos[i].id);
          for (var j = 0; j < n.length; j++) out.push(n[j]);
        }
      }
    }
    return out;
  }

  function getRootCargos() {
    var result = [];
    for (var i = 0; i < cargos.length; i++) {
      if (!cargos[i].cargo_superior_id) {
        if (!visibleCargoIds || visibleCargoIds.has(cargos[i].id)) {
          result.push(cargos[i]);
        }
      }
    }
    return result;
  }

  // Search logic - multi-word search
  function handleSearch(text) {
    setSearchText(text);
    if (!text || text.length < 2) {
      setSearchResults([]);
      return;
    }
    var words = text.toLowerCase().replace(/[.\-]/g, '').split(/\s+/).filter(function(w) { return w.length > 0; });
    var results = [];
    // Search personas
    personas.forEach(function(p) {
      var searchable = ((p.nombre || '') + ' ' + (p.apellido || '')).toLowerCase();
      var rutClean = (p.rut || '').replace(/[.\-\s]/g, '').toLowerCase();
      var allMatch = words.every(function(w) { return searchable.includes(w) || rutClean.includes(w); });
      if (allMatch) {
        var cargoName = '';
        for (var j = 0; j < cargos.length; j++) {
          if (cargos[j].id === p.cargo_id) { cargoName = cargos[j].nombre; break; }
        }
        var effGid = personaGerenciaId(p);
        var effGerNom = effGid ? (gerenciaNameMap[effGid] || p.gerencia_nombre) : p.gerencia_nombre;
        results.push({ type: 'persona', id: p.id, cargoId: p.cargo_id, label: p.nombre + ' ' + p.apellido, sub: cargoName + (effGerNom ? ' | ' + effGerNom : ''), persona: p });
      }
    });
    // Search cargos
    cargos.forEach(function(c) {
      var cargoLower = c.nombre.toLowerCase();
      var allMatch = words.every(function(w) { return cargoLower.includes(w); });
      if (allMatch) {
        var pCount = 0;
        for (var k = 0; k < personas.length; k++) {
          if (personas[k].cargo_id === c.id) pCount++;
        }
        results.push({ type: 'cargo', id: c.id, cargoId: c.id, label: c.nombre, sub: 'Nivel ' + c.nivel + ' - ' + pCount + ' persona(s)' });
      }
    });
    setSearchResults(results.slice(0, 15));
  }

  function handleSelectSearchResult(result) {
    var cargoMap = {};
    cargos.forEach(function(c) { cargoMap[c.id] = c; });
    // Expand the path to this cargo
    var newExpanded = {};
    for (var key in expanded) { newExpanded[key] = expanded[key]; }
    var current = result.cargoId;
    while (current) {
      newExpanded[current] = true;
      var c = cargoMap[current];
      current = c ? c.cargo_superior_id : null;
    }
    setExpanded(newExpanded);
    // Select the cargo
    var cargo = cargoMap[result.cargoId];
    if (cargo) {
      setSelectedCargo(cargo);
      if (result.type === 'persona') {
        setSelectedPersona(result.persona);
      }
    }
    // Auto-set gerencia/superintendencia filter from the persona
    var persona = result.persona || null;
    if (persona && persona.gerencia_id) {
      setFilterGerencia(persona.gerencia_id);
      if (persona.superintendencia_id) {
        setFilterSuperintendencia(persona.superintendencia_id);
      } else {
        setFilterSuperintendencia('');
      }
    } else {
      setFilterGerencia('');
      setFilterSuperintendencia('');
    }
    // Highlight the cargo node
    setHighlightedCargoId(result.cargoId);
    setTimeout(function() { setHighlightedCargoId(null); }, 5000);
    setSearchText('');
    setSearchResults([]);
  }

  function toggleExpand(cargoId) {
    setExpanded(function(prev) {
      var newState = {};
      for (var key in prev) { newState[key] = prev[key]; }
      newState[cargoId] = !prev[cargoId];
      return newState;
    });
  }

  function handleSelectCargo(cargo) {
    setSelectedCargo(cargo);
    var ps = getPersonasByCargo(cargo.id);
    setSelectedPersona(ps.length > 0 ? ps[0] : null);
  }

  function buildFlatList() {
    var result = [];
    var roots = getRootCargos();
    
    function addCargo(cargo, level) {
      var subs = getSubCargos(cargo.id);
      result.push({
        cargo: cargo,
        level: level,
        hasChildren: subs.length > 0,
        isExpanded: expanded[cargo.id] === true
      });
      if (expanded[cargo.id]) {
        for (var i = 0; i < subs.length; i++) {
          addCargo(subs[i], level + 1);
        }
      }
    }
    
    for (var i = 0; i < roots.length; i++) {
      addCargo(roots[i], 0);
    }
    
    return result;
  }

  function handleZoomIn() {
    setZoom(function(z) { return Math.min(150, z + 10); });
  }

  function handleZoomOut() {
    setZoom(function(z) { return Math.max(50, z - 10); });
  }

  if (loading) {
    return <div className="flex justify-center h-64"><div className="animate-spin h-8 w-8 border-b-2 border-teal-500 rounded-full"></div></div>;
  }

  var flatList = buildFlatList();
  var rootCargos = getRootCargos();

  return (
    <div className={isFullscreen ? 'fixed inset-0 z-50 bg-white flex flex-col overflow-hidden' : 'space-y-4'}>
      <div className={'flex items-center justify-between flex-wrap gap-4' + (isFullscreen ? ' px-5 py-3 border-b bg-white shrink-0' : '')}>
        {!hideCapacitaciones ? (
        <div>
          <h1 className={isFullscreen ? 'text-lg font-bold text-slate-900' : 'text-2xl font-bold text-slate-900'}>Organigrama</h1>
          {!isFullscreen && <p className="text-slate-500">Estructura organizacional y capacitaciones</p>}
        </div>
        ) : <div />}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              className={'px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ' + (viewMode === 'tree' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-600')}
              onClick={function() { setViewMode('tree'); }}
            >
              <GitBranch className="w-4 h-4" />
              Árbol
            </button>
            <button
              className={'px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ' + (viewMode === 'list' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-600')}
              onClick={function() { setViewMode('list'); }}
            >
              <List className="w-4 h-4" />
              Lista
            </button>
          </div>
          
          {viewMode === 'tree' && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button className="p-1.5 text-slate-600 hover:text-slate-900 rounded" onClick={handleZoomOut}>
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-600 w-10 text-center">{zoom}%</span>
              <button className="p-1.5 text-slate-600 hover:text-slate-900 rounded" onClick={handleZoomIn}>
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            className="p-2 text-slate-600 hover:text-teal-600 hover:bg-slate-100 rounded-lg transition-colors"
            onClick={function() { setIsFullscreen(!isFullscreen); }}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className={'grid grid-cols-1 lg:grid-cols-4 gap-4' + (isFullscreen ? ' flex-1 overflow-hidden px-5 pb-3' : '')}>
        {/* Filters */}
        <div className={'lg:col-span-4 flex flex-wrap items-center gap-3' + (isFullscreen ? ' shrink-0 pt-2' : '')}>
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="search-organigrama"
              type="text"
              placeholder="Buscar nombre, RUT o cargo..."
              className="border border-slate-300 rounded-lg pl-9 pr-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 w-[260px]"
              value={searchText}
              onChange={function(e) { handleSearch(e.target.value); }}
            />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-[360px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-[320px] overflow-y-auto">
                {searchResults.map(function(r, idx) {
                  return (
                    <button
                      key={r.type + '-' + r.id + '-' + idx}
                      data-testid={'search-result-' + idx}
                      className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b border-slate-100 last:border-0 flex items-center gap-2"
                      onClick={function() { handleSelectSearchResult(r); }}
                    >
                      <div className={'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white ' + (r.type === 'persona' ? 'bg-teal-500' : 'bg-copper-500')}>
                        {r.type === 'persona' ? (r.label[0] || '').toUpperCase() : 'C'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.label}</p>
                        <p className="text-[10px] text-slate-500 truncate">{r.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="h-6 w-px bg-slate-300"></div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Filter className="w-4 h-4" />
          </div>
          <select
            data-testid="filter-gerencia"
            disabled={lockedGerencia}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-w-[200px] disabled:bg-slate-100 disabled:cursor-not-allowed"
            value={filterGerencia}
            onChange={function(e) { setFilterGerencia(e.target.value); setFilterSuperintendencia(''); }}
          >
            {!lockedGerencia && <option value="">Todas las Gerencias</option>}
            {gerenciaOptions.map(function(g) {
              return <option key={g.id} value={g.id}>{g.nombre}</option>;
            })}
          </select>
          <select
            data-testid="filter-superintendencia"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-w-[220px]"
            value={filterSuperintendencia}
            onChange={function(e) { setFilterSuperintendencia(e.target.value); }}
          >
            <option value="">Todas las Superintendencias</option>
            {filteredSuperintendencias.map(function(s) {
              return <option key={s.id} value={s.id}>{s.nombre}</option>;
            })}
          </select>
          {isFiltered && !lockedGerencia && (
            <button
              data-testid="clear-filters-btn"
              className="text-sm text-teal-600 hover:text-teal-800 font-medium underline"
              onClick={function() { setFilterGerencia(''); setFilterSuperintendencia(''); }}
            >
              Limpiar filtros
            </button>
          )}
          {isFiltered && (
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {filteredPersonas.length} personas
            </span>
          )}
        </div>
        <div className={(isFullscreen || hideDetalle) ? 'lg:col-span-4 flex-1 overflow-hidden' : 'lg:col-span-3'}>
          <Card className={'border border-slate-200 shadow-sm' + (isFullscreen ? ' h-full flex flex-col' : '')}>
            <CardContent className={'p-4' + (isFullscreen ? ' flex-1 overflow-auto' : '')}>
              {viewMode === 'tree' ? (
                <div 
                  className="overflow-x-auto py-8 px-4"
                  style={{ transform: 'scale(' + (zoom / 100) + ')', transformOrigin: 'top left' }}
                >
                  <div className="flex flex-col items-center gap-4 min-w-max">
                    {rootCargos.map(function(cargo) {
                      return (
                        <TreeNode
                          key={cargo.id}
                          cargo={cargo}
                          areas={areas}
                          personas={personas}
                          cargos={cargos}
                          expanded={expanded}
                          onToggle={toggleExpand}
                          onSelect={handleSelectCargo}
                          selectedId={selectedCargo ? selectedCargo.id : null}
                          visibleCargoIds={visibleCargoIds}
                          filteredPersonas={filteredPersonas}
                          isFiltered={isFiltered}
                          cargoIdsWithFilteredPersonas={cargoIdsWithFilteredPersonas}
                          highlightedCargoId={highlightedCargoId}
                          onShowPersonas={function(c, p) { setPersonasModal({ open: true, cargo: c, personas: p }); }}
                          lockedGerencia={lockedGerencia}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {flatList.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No hay cargos registrados</p>
                  )}
                  {flatList.map(function(item) {
                    var cargo = item.cargo;
                    var cargoPersonas = getPersonasByCargo(cargo.id);
                    var isSelected = selectedCargo && selectedCargo.id === cargo.id;
                    var paddingLeft = item.level * 24 + 12;
                    
                    return (
                      <div
                        key={cargo.id}
                        className={'flex items-center gap-2 p-3 hover:bg-slate-50 cursor-pointer ' + (isSelected ? 'bg-teal-50 border-l-4 border-l-teal-500' : '')}
                        style={{ paddingLeft: paddingLeft + 'px' }}
                        onClick={function() { handleSelectCargo(cargo); }}
                        data-testid={'cargo-' + cargo.id}
                      >
                        {item.hasChildren ? (
                          <button 
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200" 
                            onClick={function(e) { e.stopPropagation(); toggleExpand(cargo.id); }}
                          >
                            {item.isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                          </button>
                        ) : <div className="w-6"></div>}
                        
                        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                          <Network className="w-4 h-4 text-teal-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{cargo.nombre}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {getAreaName(cargo.area_id)}
                            </span>
                            {cargoPersonas.length > 0 && (
                              <span className="text-teal-600 font-medium flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {cargoPersonas.map(function(p) { return p.nombre + ' ' + p.apellido; }).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-700">
                          Nivel {cargo.nivel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {!isFullscreen && !hideDetalle && (
        <div className="lg:col-span-1">
          <Card className="border border-slate-200 shadow-sm sticky top-20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Network className="w-5 h-5 text-teal-500" />
                Detalle
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCargo ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Cargo</p>
                    <p className="text-lg font-semibold text-slate-900">{selectedCargo.nombre}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Área</p>
                    <p className="text-sm text-slate-700">{getAreaName(selectedCargo.area_id)}</p>
                  </div>
                  
                  {getPersonasByCargo(selectedCargo.id).length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-2">
                        <Users className="w-3 h-3 inline mr-1" />
                        Personas
                      </p>
                      <div className="space-y-2 max-h-[420px] overflow-y-auto">
                        {getPersonasByCargo(selectedCargo.id).map(function(p) {
                          var isSelectedP = selectedPersona && selectedPersona.id === p.id;
                          
                          function copyEmail(e) {
                            e.stopPropagation();
                            if (p.email) {
                              navigator.clipboard.writeText(p.email);
                              toast.success('Correo copiado');
                            }
                          }
                          
                          return (
                            <button
                              key={p.id}
                              onClick={function() { setSelectedPersona(p); }}
                              className={'w-full text-left p-2 rounded-lg ' + (isSelectedP ? 'bg-teal-100 border border-teal-300' : 'bg-slate-50 hover:bg-slate-100')}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-[10px] font-bold text-white">{(p.nombre || '')[0]}{(p.apellido || '')[0]}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-700">{p.nombre} {p.apellido}</p>
                                  {p.jefe_nombre && (
                                    <p className="text-[10px] text-slate-500 mt-0.5">
                                      Reporta a: <span className="font-medium text-teal-700">{p.jefe_nombre}</span>
                                      {p.jefe_cargo_nombre && <span className="text-slate-400"> ({p.jefe_cargo_nombre})</span>}
                                    </p>
                                  )}
                                  {p.email && (
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <p className="text-[10px] text-slate-500 truncate flex-1">{p.email}</p>
                                      <button
                                        onClick={copyEmail}
                                        className="p-1 hover:bg-slate-200 rounded flex-shrink-0"
                                        title="Copiar correo"
                                      >
                                        <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {!hideCapacitaciones && selectedPersona && (
                    <div className="border-t pt-4">
                      <p className="text-xs text-slate-500 uppercase mb-2 flex items-center gap-1">
                        <GraduationCap className="w-3 h-3" />
                        Capacitaciones de {selectedPersona.nombre}
                      </p>
                      <PersonaCapacitaciones personaId={selectedPersona.id} onUpdate={fetchData} />
                    </div>
                  )}
                  
                  {getPersonasByCargo(selectedCargo.id).length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-2 bg-slate-50 rounded-lg">Sin personas asignadas</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">Selecciona un cargo</p>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Modal personas del cargo */}
      {personasModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={function() { setPersonasModal({ open: false, cargo: null, personas: [] }); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={function(e) { e.stopPropagation(); }}>
            <div className="px-6 py-4 bg-teal-600">
              <h3 className="text-base font-bold text-white">{personasModal.cargo?.nombre}</h3>
              <p className="text-xs text-teal-100">{personasModal.personas.length} persona{personasModal.personas.length !== 1 ? 's' : ''} asignada{personasModal.personas.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y max-h-[400px] overflow-y-auto">
              {personasModal.personas.map(function(p, idx) {
                var pGid = personaGerenciaId(p);
                var pGerNom = pGid ? (gerenciaNameMap[pGid] || p.gerencia_nombre) : p.gerencia_nombre;
                return (
                  <div key={idx} className="flex items-center gap-3 px-6 py-3 hover:bg-slate-50">
                    <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{p.nombre} {p.apellido}</p>
                      <p className="text-xs text-slate-400">{p.rut || ''}{pGerNom ? ' · ' + pGerNom : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-3 border-t">
              <button onClick={function() { setPersonasModal({ open: false, cargo: null, personas: [] }); }}
                className="w-full py-2 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
